/**
 * @file EmotionRatingSlider.tsx
 * @brief Mood slider + line-art face for meeting feedback (1–5) — mobile parity.
 *
 * @details
 * Web port of FRONTEND EmotionRatingSlider. Continuous-feel range input snaps
 * to five ratings; mood label + SVG face follow. Submitters still POST
 * overallRating 1–5 to `/api/feedback/session`.
 *
 * @dependencies
 * - shared/theme/cn
 *
 * @references
 * - FRONTEND/src/shared/components/EmotionRatingSlider.tsx
 * - MeetingFeedbackPrompt.tsx
 *
 * @author DES
 * @date 30-07-2026
 * @updated 30-07-2026
 */

import { cn } from "@/shared/theme/cn";

export type EmotionMood = {
  rating: 1 | 2 | 3 | 4 | 5;
  label: string;
  accent: string;
};

// EMOTION_MOODS / moodForRating are module-private: neither had a consumer, and
// value exports beside a component cost the module its Fast Refresh. The
// `EmotionMood` type above stays exported — types are erased, so they don't
// affect it. (02-08-2026)
const EMOTION_MOODS: EmotionMood[] = [
  { rating: 1, label: "Terrible", accent: "#D6455A" },
  { rating: 2, label: "Poor", accent: "#D97A55" },
  { rating: 3, label: "Okay", accent: "#8A8674" },
  { rating: 4, label: "Good", accent: "#2F9B6A" },
  { rating: 5, label: "Great", accent: "#7AB82E" },
];

function moodForRating(
  rating: number,
  fallback: EmotionMood = EMOTION_MOODS[2],
): EmotionMood {
  if (rating >= 1 && rating <= 5) return EMOTION_MOODS[rating - 1];
  return fallback;
}

function EmotionFace({
  rating,
  color,
  size = 140,
}: {
  rating: number;
  color: string;
  size?: number;
}) {
  const r = rating >= 1 && rating <= 5 ? rating : 3;
  const stroke = Math.max(3.2, size * 0.028);
  const angryEyes = r <= 2;
  const mouth =
    r === 1
      ? "M32 72 Q50 58 68 72"
      : r === 2
        ? "M34 70 Q50 62 66 70"
        : r === 3
          ? "M34 72 Q50 78 66 72"
          : r === 4
            ? "M30 68 Q50 86 70 68"
            : "M26 66 Q50 94 74 66";

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 100 100"
      aria-hidden
      className="shrink-0"
    >
      {angryEyes ? (
        <>
          <path
            d="M28 38 L42 44 L28 50"
            stroke={color}
            strokeWidth={stroke}
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="none"
          />
          <path
            d="M72 38 L58 44 L72 50"
            stroke={color}
            strokeWidth={stroke}
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="none"
          />
        </>
      ) : (
        <>
          <circle cx={36} cy={44} r={4.2} fill={color} />
          <circle cx={64} cy={44} r={4.2} fill={color} />
        </>
      )}
      <path
        d={mouth}
        stroke={color}
        strokeWidth={stroke}
        strokeLinecap="round"
        fill="none"
      />
    </svg>
  );
}

type Props = {
  /** Committed rating 1–5, or 0 before the user slides. */
  rating: number;
  onChange: (rating: number) => void;
  className?: string;
  accessibilityLabel?: string;
};

export function EmotionRatingSlider({
  rating,
  onChange,
  className,
  accessibilityLabel = "Rate this meeting",
}: Props) {
  const displayRating = rating >= 1 && rating <= 5 ? rating : 3;
  const mood = moodForRating(displayRating);
  const faceColor = mood.accent;

  const commit = (raw: number) => {
    const next = Math.round(raw);
    if (next >= 1 && next <= 5) onChange(next);
  };

  return (
    <div
      className={cn(
        "flex w-full flex-col items-center px-2 pb-2 pt-1",
        className,
      )}
    >
      <p
        className="mb-2 min-h-8 text-center text-2xl font-bold tracking-tight"
        style={{ color: faceColor }}
        aria-live="polite"
      >
        {rating >= 1 ? mood.label : "Slide to rate"}
      </p>
      <div className="mb-3 flex min-h-[132px] items-center justify-center">
        <EmotionFace rating={displayRating} color={faceColor} size={140} />
      </div>
      <input
        type="range"
        min={1}
        max={5}
        step={1}
        value={displayRating}
        aria-label={accessibilityLabel}
        aria-valuemin={1}
        aria-valuemax={5}
        aria-valuenow={displayRating}
        aria-valuetext={rating >= 1 ? mood.label : "Not rated yet"}
        className="h-10 w-full max-w-sm cursor-pointer"
        style={{ accentColor: faceColor }}
        onPointerDown={() => {
          if (rating < 1) onChange(displayRating);
        }}
        onChange={(e) => commit(Number(e.target.value))}
      />
      <p className="mt-1 min-h-4 text-center text-xs font-medium text-muted-foreground opacity-85">
        {rating >= 1 ? `${displayRating} of 5` : "Drag to pick a mood"}
      </p>
    </div>
  );
}
